import {
  computed,
  Directive,
  inject,
  input,
  linkedSignal,
  model,
  output,
  TemplateRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { PopupComponent } from '@shared/components/popup/base/popup.component';
import { get } from 'lodash-es';

/**
 * Inheritance-only base for searchable option popups.
 * `T` is the option type, `V` the (possibly mapped) form value type.
 * Both default to `any` because consumers bind heterogeneous config-driven option lists.
 */

@Directive()
export abstract class SearchPopupHandlerComponent<T = any, V = T> extends PopupComponent<V> {
  readonly labelKey = input<string>();

  readonly valueKey = input<string | null>();

  readonly loading$ = input<Observable<boolean>>();

  readonly options = input<T[]>([]);

  /**
   * Accepts either an option object or a bare option VALUE (consumers bind both);
   * normalized via {@link selectedOption}. Updated internally on select, hence a model.
   */
  readonly model = model<T | V | undefined>(undefined);

  readonly optionTpl = input<TemplateRef<unknown>>();

  readonly originTpl = input<TemplateRef<unknown>>();

  readonly mapValueTo = input<(value: T) => V>();

  readonly displayWith = input<(value: T) => string>();

  readonly selectChange = output<V | null>();

  readonly searchControl = new FormControl('', { nonNullable: true });

  protected translate = inject(TranslateService);

  private readonly searchTerm = toSignal(this.searchControl.valueChanges.pipe(debounceTime(300)), {
    initialValue: '',
  });

  /** Options matching the current search term. */
  readonly filteredOptions = computed(() => {
    const term = this.searchTerm().trim();
    const options = this.options();
    if (!term) {
      return options;
    }
    const terms = term.split(' ');
    return options.filter((option) =>
      terms.every((it) => this.searchableLabel(option).includes(it.toLowerCase().trim())),
    );
  });

  readonly visibleOptions = computed(() => this.filteredOptions().length || 1);

  /** Index of the keyboard-highlighted option; resets whenever the option list changes. */
  readonly activeIndex = linkedSignal<T[], number>({
    source: this.filteredOptions,
    computation: () => -1,
  });

  /**
   * The model normalized to an option object — resolves bare values against the
   * options list (re-resolving when async options arrive after the value was set).
   */
  readonly selectedOption = computed<T | undefined>(() => {
    const raw = this.model();
    if (raw == null) {
      return undefined;
    }
    const options = this.options();
    const resolved =
      options.find((option) => this.optionValue(option) === (raw as V)) ??
      options.find((option) => this.optionId(option) === this.optionId(raw as T));
    if (resolved) {
      return resolved;
    }
    // A bare value with no matching option stays unresolved (options may arrive later);
    // an object is assumed to BE an option even when absent from the current list.
    return typeof raw === 'object' ? (raw as T) : undefined;
  });

  get label(): string {
    return this.displayLabel(this.selectedOption());
  }

  /** String-coerced display value — safe to pipe through translate in templates. */
  displayLabel(option: T | undefined | null): string {
    const label = this.displayValue(option);
    if (label == null) {
      return '';
    }
    return typeof label === 'string' ? label : String(label);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    const options = this.filteredOptions();
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex.update((index) => Math.min(index + 1, options.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex.update((index) => Math.max(index - 1, 0));
        break;
      case 'Enter': {
        event.preventDefault();
        const option = options[this.activeIndex()];
        if (option !== undefined) {
          this.select(option);
        }
        break;
      }
    }
  }

  displayValue(option: T | undefined | null): unknown {
    if (option == null) {
      return '';
    }
    const labelKey = this.labelKey();
    const displayWith = this.displayWith();
    if (!labelKey && !displayWith) {
      return option;
    }
    if (displayWith) {
      return displayWith(option);
    }
    return get(option, labelKey!);
  }

  optionValue(option: T | undefined | null): V | null {
    if (option == null) {
      return option as V | null;
    }
    const valueKey = this.valueKey();
    if (valueKey) {
      return get(option, valueKey) as V;
    }
    const mapValueTo = this.mapValueTo();
    if (mapValueTo) {
      return mapValueTo(option);
    }
    return option as unknown as V;
  }

  optionId(option: T | undefined | null): unknown {
    const valueKey = this.valueKey();
    if (!valueKey || option == null) {
      return option;
    }
    return get(option, valueKey);
  }

  override close() {
    super.close();
    this.searchControl.reset();
  }

  /** User-driven selection — notifies the form. Programmatic writes go through writeValue. */
  select(option: T): void {
    if (this.optionValue(option) !== this.optionValue(this.selectedOption())) {
      this.model.set(option);
      const value = this.optionValue(option);
      this.onChanged(value);
      this.selectChange.emit(value);
    }
    this.close();
  }

  isActive(option: T): boolean {
    const selected = this.selectedOption();
    if (!selected) {
      return false;
    }
    return this.optionId(option) === this.optionId(selected);
  }

  /**
   * CVA contract: apply the value WITHOUT re-emitting onChange — a programmatic
   * `setValue`/`patchValue` must not mark the form dirty or loop back into valueChanges.
   */
  override writeValue(obj: V | null): void {
    this.model.set(obj ?? undefined);
  }

  private searchableLabel(option: T): string {
    const label = this.displayValue(option);
    const translated = typeof label === 'string' ? this.translate.instant(label) : label;
    return String(translated ?? '').toLowerCase();
  }
}
